// [목적] 랜딩 페이지(/) 전용 시각 토큰 — packages/ui 토큰으로 표현 불가한 hex/px 의 단일 SoT
// [이유] CLAUDE.md §6-3 디자인 토큰 강제 정책. docs/플리오랩/강원캔버스-웹.html 의 :root 변수 중
//         packages/ui 가 커버하지 못하는 마케팅 페이지 전용 값(brand-2, brand-3, accent-purple,
//         3단 그림자, 폰 목업 본체색, 그라데이션 stop) 을 본 파일에서만 hex/px 직접 입력 허용.
// [사이드 이팩트] LandingPage.tsx 의 모든 색·그림자 입력은 본 토큰 경유. 외부 파일에서의 hex 입력은 금지.
//                 packages/ui 이관 Spec 진입 시 import 경로만 교체 — 호출부 무수정.
// [수정 시 고려사항] auth/tokens.ts 와 동일한 명명 export 정책. 그라데이션 값은 문자열로 박제하여
//                   사용처에서 백틱 보간 없이 직접 사용 가능. WCAG 검산은 본문 ink(packages/ui) 토큰을 사용한다.
//                   [2026-09-18] brandStrong/warmStrong 은 packages/ui brand[600]/warm[600] 재수출(신규 hex 아님) —
//                   대비 게이트는 packages/ui/test/contrast.spec.ts 에서 검증. avatarBrand 도 brand[600] 재사용.

import { tokens } from '@gangwoncanvas/ui';

const c = tokens.color;
const L = 'light' as const;

/**
 * [목적] 색상 토큰 — packages/ui 의 brand/accent/warm/paper/ink/line 과 보완
 * [이유] HTML --brand-2(#4A8AF0), --shadow-*, 폰 목업 본체(#1a1a1a) 등 packages/ui 미포함 값
 * [사이드 이팩트] LandingPage 외 파일에서 import 금지
 */
export const LANDING_COLORS = {
  // packages/ui 와 의미 동일 — 가독성 위해 재export
  bg: c.paper[1][L], //         #FAF9F7
  bgSoft: c.paper[2][L], //     #F2F0EB
  paper: c.paper[0][L], //      #FFFFFF
  ink: c.ink.high[L], //        #29261B
  inkMid: c.ink.mid[L], //      #5C5849
  inkLow: c.ink.low[L], //      #9C9686
  border: c.line.base[L], //    #E8E2D5
  borderSoft: c.line.soft[L], //#F0EBE0
  brand: c.brand[500][L], //    #1F6FEB
  green: c.accent[500][L], //   #2BB673
  warm: c.warm[500][L], //      #F2A65A
  // [2026-09-18] 소형 브랜드 텍스트(섹션 이거브로우 FEATURES/GALLERY/FAQ 등) 전용 진한 브랜드.
  // brand(#1F6FEB) 는 paper[1] 위 12px 텍스트 기준 4.4:1 로 AA 미달 — brandStrong(#1557BE) 은 6.36:1.
  brandStrong: c.brand[600][L],
  // [2026-09-18] 별점(★) 텍스트 전용 진한 warm. warm(#F2A65A) 는 흰 배경 위 2.02:1 로 AA 미달 —
  // warmStrong(#A4630F) 는 4.81:1.
  warmStrong: c.warm[600][L],

  // 랜딩 전용 — packages/ui 미포함
  brand2: '#4A8AF0', //         hero 그라데이션 우측 stop
  brand3: '#6BA3F5', //         final-cta 그라데이션 마지막 stop
  accentPurple: '#8B5CF6', //   양양 서핑 폴리오 카드 커버 — 마케팅 다양성 한정
  accentWarmDeep: '#FFB876', // 서연님 아바타 그라데이션 끝 stop
  greenDeep: '#34C57F', //      정훈님 아바타 그라데이션 끝 stop
  phoneBody: '#1a1a1a', //      iPhone 베젤/노치 placeholder
  phoneBodyHover: '#1a1812', // nav-cta hover bg
  fileGrid1F0EBE0: '#F0EBE0', //경계선 강조용 (border-2 와 동값)
  cardOverlay: 'rgba(0,0,0,.04)', //    logo-mark border
  whiteScrollbar: 'rgba(0,0,0,.12)', // scrollbar thumb
  whiteAlpha85: 'rgba(250,249,247,.85)', // header backdrop
  whiteAlpha15: 'rgba(255,255,255,.15)', // final-cta white glow
  whiteAlpha20: 'rgba(255,255,255,.20)', // store-btn border
  whiteAlpha95: 'rgba(255,255,255,.95)', // folio-cover days chip
  blackAlpha45: 'rgba(0,0,0,.45)', //    store-btn bg
  blackAlpha60: 'rgba(0,0,0,.60)', //    store-btn hover
  ctaShadow: '0 12px 32px rgba(0,0,0,.18)', // final cta primary
  brandGlow: 'rgba(31,111,235,.07)', //  hero radial 1
  greenGlow: 'rgba(43,182,115,.05)', //  hero radial 2
  brandUnderline: 'rgba(31,111,235,.18)', // hero h1 em 마커
  brandShadowSoft: 'rgba(31,111,235,.3)', // btn-primary shadow
  brandShadowMid: 'rgba(31,111,235,.4)', //  btn-primary hover shadow
  notchSurround: 'rgba(0,0,0,.04)', //   phone shadow ring
  cardHoverDark: 'rgba(20,24,31,.18)', //phone box-shadow
} as const;

/**
 * [목적] 그림자 3단 — HTML --shadow-1/2/3 1:1 매칭
 * [이유] packages/ui shadow 는 card/modal 2단만 — 마케팅 페이지 hover lift 단계 부족
 * [사이드 이팩트] packages/ui ShadowTokens 확장 시 본 토큰을 deprecate 가능
 */
export const LANDING_SHADOWS = {
  s1: '0 1px 2px rgba(20,24,31,.04)',
  s2: '0 4px 16px rgba(20,24,31,.06)',
  s3: '0 12px 40px rgba(20,24,31,.10)',
  phone: '0 30px 80px rgba(20,24,31,.18), 0 0 0 1px rgba(0,0,0,.04)',
  primaryBtn: '0 6px 20px rgba(31,111,235,.3)',
  primaryBtnHover: '0 10px 28px rgba(31,111,235,.4)',
} as const;

/**
 * [목적] 그라데이션 — HTML linear-gradient 4종을 박제
 * [이유] sx 백틱 보간 회피 + 가독성. HTML 시안과 1:1 일치 보존 (R-4 시각 회귀 0건 기조)
 * [사이드 이팩트] 사용처는 sx background 에 그대로 주입
 */
export const LANDING_GRADIENTS = {
  logoMark: 'linear-gradient(135deg, #1F6FEB, #4A8AF0)', //              로고 mark
  chatBubbleMe: 'linear-gradient(135deg, #1F6FEB, #4A8AF0)', //          내 말풍선
  chatCta: 'linear-gradient(135deg, #1F6FEB, #4A8AF0)', //               챗 CTA
  heroRadial:
    'radial-gradient(900px circle at 80% 20%, rgba(31,111,235,.07), transparent 50%), radial-gradient(700px circle at 10% 80%, rgba(43,182,115,.05), transparent 50%)',
  // [2026-09-18 대비 상향] 구 stop(#4A8AF0/#6BA3F5) 위에서는 흰 텍스트가 3.4:1·2.57:1 로 AA 미달이었다.
  // 신규 stop 은 흰 텍스트 기준 6.69 / 5.85 / 4.63:1 로 전 구간 AA 충족(브랜드 블루 계열 유지).
  finalCta: 'linear-gradient(135deg, #1557BE 0%, #1A5FD0 50%, #1F6FEB 100%)',
  finalCtaGlow: 'radial-gradient(circle, rgba(255,255,255,.15) 0%, transparent 60%)',
  // 폴리오 커버 4종 — 마케팅 시각 다양성 목적, packages/ui 의미 매칭 불가
  folioCover1: 'linear-gradient(135deg,#1F6FEB,#1F6FEBDD)',
  folioCover2: 'linear-gradient(135deg,#2BB673,#2BB673DD)',
  folioCover3: 'linear-gradient(135deg,#F2A65A,#F2A65ADD)',
  folioCover4: 'linear-gradient(135deg,#8B5CF6,#8B5CF6DD)',
  // 사용자 아바타 그라데이션 3종
  // [2026-09-18 대비 상향] 흰 이니셜 텍스트 기준 — avatarGreen/avatarWarm 은 끝 stop(#34C57F/#FFB876)
  // 위에서 2.22/1.7:1 로 AA 미달이었다. 두 그라데이션은 시안 톤을 유지한 채 아바타 텍스트 색을
  // ink.high(다크)로 전환해 해소(LandingPage.tsx TestimonialsSection avatarFg 참조, 5.8~8.9:1).
  // avatarBrand 는 흰 텍스트를 유지해야 해서(다크 텍스트 사용 시 첫 stop 위 3.27:1 로 오히려 악화)
  // 끝 stop 을 brand[600](#1557BE, 흰 텍스트 기준 6.69:1)으로 교체 — 톤은 유지하되 더 짙은 블루로 마감.
  avatarBrand: `linear-gradient(135deg,${c.brand[500][L]},${c.brand[600][L]})`,
  avatarGreen: 'linear-gradient(135deg,#2BB673,#34C57F)',
  avatarWarm: 'linear-gradient(135deg,#F2A65A,#FFB876)',
  // feature 카드 아이콘 배경 (연한 톤)
  featureBgBlue: '#EAF4FF',
  featureBgGreen: '#E8F8EF',
  featureBgWarm: '#FFF4E8',
  featureBgPurple: '#F5F0FF',
  featureBgRed: '#FFF0F0',
  featureBgGray: '#EEF2F7',
} as const;

/**
 * [목적] 간격·크기 토큰 — packages/ui spacing 으로 표현 어려운 마케팅 페이지 고정값
 * [이유] section padding(100/64px), max-width(1200px), 폰 목업 크기 등은 packages/ui 4단위 체계 외
 * [사이드 이팩트] sx 에서 숫자 직접 입력 회피
 */
export const LANDING_LAYOUT = {
  containerMax: 1200,
  containerPx: 24,
  sectionPyDesktop: 100,
  sectionPyMobile: 64,
  headerHeight: 64,
  heroPtDesktop: 160,
  heroPbDesktop: 100,
  phoneWidth: 280,
  phoneHeight: 580,
} as const;

/**
 * [목적] keyframes — HTML @keyframes float 1:1
 * [이유] hero float-chip 3개의 상하 부유 애니메이션
 * [사이드 이팩트] LandingPage sx 에서 animation 속성으로 참조 (style 태그 별도 주입)
 */
export const LANDING_KEYFRAMES = {
  float: `
@keyframes landing-float {
  0%,100% { transform: translateY(0) rotate(-1deg); }
  50% { transform: translateY(-12px) rotate(1deg); }
}
`,
} as const;
